import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Select, MenuItem, FormControl, FormHelperText,
  Button, IconButton, Collapse, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MediaPreviewOverlay from '../common/MediaPreviewOverlay';
import CustomDatePicker from '../form-components/CustomDatePicker';
import { textFieldSx, selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';
import DropdownTextField from '../form-components/DropdownTextField';
import { useCurrency } from '../../context/CurrencyContext';
import { useLocalization } from '../../context/LocalizationContext';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { hasRequiredName, describeMediaUrlStatus } from '../../utils/validators';
import useUrlValidation from '../../hooks/useUrlValidation';
import UrlStatusIcon from '../common/UrlStatusIcon';
import {
  createMerchandiseType, updateMerchandiseType, deleteMerchandiseType,
  createMerchandiseCategory, updateMerchandiseCategory, deleteMerchandiseCategory,
} from '../../api/itemApi';
import { extractErrorMessage } from '../../utils/errorMessage';
import ConfirmModal from '../form-components/ConfirmModal';

// ── Creation defaults ─────────────────────────────────────────────────────────
const DEFAULT_ENTITY_ID       = null;   // not exposed in UI
const THUMBNAIL_MEDIA_TYPE_ID = 1;      // image
const BADGE_MEDIA_TYPE_ID     = 2;      // badge image
const THUMBNAIL_MEDIA_ID      = null;   // no upload on create
const BADGE_MEDIA_ID          = null;   // no upload on create
const TICKET_SEAT             = null;   // phase 2
const TICKET_SECTION          = null;   // phase 2
const TICKET_VALID_FROM       = null;   // phase 2
const TICKET_VALID_TO         = null;   // phase 2
const TICKET_IS_TRANSFERABLE  = false;  // always false

// ── Form factory ──────────────────────────────────────────────────────────────
const makeEmptyForm = () => ({
  itemTypeId:               '',
  isAvailable:              true,
  nameKey:                  '',
  descriptionKey:           '',
  nameLocalizations:        [{ localeId: 'en_us', value: '' }],
  descriptionLocalizations: [{ localeId: 'en_us', value: '' }],
  baseCost:                 '',
  taxId:                    '',
  taxMode:                  'Exclusive',
  sku:                      '',
  merchandise: {
    merchandiseTypeId:    '',
    merchandiseCategoryId: '',
    assetUrl:             '',
    thumbnailMedia: {
      mediaTypeId:        THUMBNAIL_MEDIA_TYPE_ID,
      linkLocalizations:  [{ localeId: 'en_us', mediaLink: '' }],
    },
  },
  ticket: {
    eventId: '',
    ticketTypeId: '',
    validFrom: '',
    validTo: '',
    stockQuantity: '',
    thumbnailMedia: {
      mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
      linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }],
    },
  },
  badge: {
    media: {
      mediaTypeId:       BADGE_MEDIA_TYPE_ID,
      linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }],
    },
  },
  avatar: {
    assetUrl: '',
    thumbnailMedia: {
      mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
      linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }],
    },
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const computePrice = (baseCost, taxRate, taxMode) => {
  const price = parseFloat(baseCost) || 0;
  const rate  = parseFloat(taxRate)  || 0;
  if (taxMode === 'Exclusive') {
    const taxAmount = price * rate / 100;
    return { subtotal: price + taxAmount, taxAmount, price };
  }
  // Inclusive: baseCost already contains tax — back-calculate the net base
  const netBase   = rate > 0 ? price / (1 + rate / 100) : price;
  const taxAmount = price - netBase;
  return { subtotal: price, taxAmount, price: netBase };
};

const generateSKU = (name, typeLabel) => {
  const part1 = (name || '').replace(/[^a-z0-9]/gi, '').slice(0, 4).toUpperCase().padEnd(4, 'X');
  const part2 = (typeLabel || '').replace(/[^a-z0-9]/gi, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
  const rand  = Math.floor(Math.random() * 9000 + 1000);
  return `TIFO-${part1}-${part2}${rand}`;
};

const endOfDay = (dateStr) => dateStr ? `${dateStr}T23:59:59` : null;

const toNullableInt = (value) => (value === '' || value === null || value === undefined) ? null : Number(value);

const buildPayload = (form, factor, currencyId, isPrizeOnly = false) => {
  const typeId = Number(form.itemTypeId);
  const base = {
    itemTypeId:               typeId,
    isAvailable:              form.isAvailable,
    // A prize item is never purchasable and never has a price row at all (see
    // ItemRepository.AddAsync's IsPrizeOnly branch) — currencyId is meaningless for it and
    // is omitted rather than sent as whatever activeCurrency happens to resolve to (or
    // doesn't: a space with no currency configured yet would send currencyId undefined,
    // which the backend reads as 0 and rejects with "Please select a currency.").
    baseCost:                 isPrizeOnly ? 0 : Math.round((parseFloat(form.baseCost) || 0) * factor),
    taxCategoryId:            form.taxId || null,
    taxIsInclusive:           form.taxId ? form.taxMode === 'Inclusive' : null,
    entityId:                 DEFAULT_ENTITY_ID,
    nameLocalizations:        form.nameLocalizations,
    descriptionLocalizations: form.descriptionLocalizations,
    ...(isPrizeOnly ? { isPrizeOnly: true } : { currencyId }),
  };

  if (typeId === 1) {
    const hasThumbnail = form.merchandise.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      merchandise: {
        merchandiseTypeId:     Number(form.merchandise.merchandiseTypeId)     || null,
        merchandiseCategoryId: Number(form.merchandise.merchandiseCategoryId) || null,
        assetUrl:              form.merchandise.assetUrl || null,
        thumbnailMediaId:      THUMBNAIL_MEDIA_ID,
        ...(hasThumbnail && {
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: form.merchandise.thumbnailMedia.linkLocalizations,
          },
        }),
      },
    };
  }
  if (typeId === 2) {
    const hasTicketThumbnail = form.ticket.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      ticket: {
        eventId:        Number(form.ticket.eventId) || null,
        ticketTypeId:   Number(form.ticket.ticketTypeId) || null,
        seat:           TICKET_SEAT,
        section:        TICKET_SECTION,
        validFrom:      form.ticket.validFrom || null,
        validTo:        endOfDay(form.ticket.validTo),
        isTransferable: TICKET_IS_TRANSFERABLE,
        stockQuantity:  toNullableInt(form.ticket.stockQuantity),
        ...(hasTicketThumbnail && {
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: form.ticket.thumbnailMedia.linkLocalizations,
          },
        }),
      },
    };
  }
  if (typeId === 3) {
    const hasBadgeMedia = form.badge.media.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      badge: {
        mediaId: BADGE_MEDIA_ID,
        ...(hasBadgeMedia && {
          media: {
            mediaTypeId:       BADGE_MEDIA_TYPE_ID,
            linkLocalizations: form.badge.media.linkLocalizations,
          },
        }),
      },
    };
  }
  if (typeId === 6) {
    const hasThumbnail = form.avatar.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      avatar: {
        assetUrl: form.avatar.assetUrl || null,
        thumbnailMediaId: THUMBNAIL_MEDIA_ID,
        ...(hasThumbnail && {
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: form.avatar.thumbnailMedia.linkLocalizations,
          },
        }),
      },
    };
  }
  // Reward (4) and Digital Collectible (5) — no type-specific block
  return base;
};

const buildUpdatePayload = (form, factor, currencyId, isPrizeOnly = false) => {
  const typeId = Number(form.itemTypeId);
  const base = {
    itemTypeId:     typeId,
    isAvailable:    form.isAvailable,
    taxCategoryId:  form.taxId || null,
    taxIsInclusive: form.taxId ? form.taxMode === 'Inclusive' : null,
    nameLocalizations: { key: form.nameKey, values: form.nameLocalizations },
    descriptionLocalizations: form.descriptionLocalizations.some(l => l.value?.trim())
      ? { key: form.descriptionKey || null, values: form.descriptionLocalizations }
      : null,
    // is_price_only is immutable after creation (UpdateItemDto has no such field — the
    // backend simply never looks at it here), and a prize has no price row to upsert in
    // the first place, so `prices` is omitted entirely rather than upserting one with
    // whatever currency happens to be resolvable.
    ...(isPrizeOnly ? {} : { prices: [{ currencyId, baseCost: Math.round((parseFloat(form.baseCost) || 0) * factor) }] }),
  };

  if (typeId === 1) {
    const hasThumbnail = form.merchandise.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      merchandise: {
        merchandiseTypeId:     Number(form.merchandise.merchandiseTypeId)     || null,
        merchandiseCategoryId: Number(form.merchandise.merchandiseCategoryId) || null,
        assetUrl:              form.merchandise.assetUrl || null,
        ...(hasThumbnail
          ? {
              thumbnailMedia: {
                mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
                linkLocalizations: form.merchandise.thumbnailMedia.linkLocalizations,
              },
            }
          : { clearThumbnail: true }),
      },
    };
  }
  if (typeId === 2) {
    const hasTicketThumbnail = form.ticket.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      ticket: {
        eventId:        Number(form.ticket.eventId) || null,
        ticketTypeId:   Number(form.ticket.ticketTypeId) || null,
        validFrom:      form.ticket.validFrom || null,
        validTo:        endOfDay(form.ticket.validTo),
        isTransferable: TICKET_IS_TRANSFERABLE,
        stockQuantity:  toNullableInt(form.ticket.stockQuantity),
        ...(hasTicketThumbnail
          ? {
              thumbnailMedia: {
                mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
                linkLocalizations: form.ticket.thumbnailMedia.linkLocalizations,
              },
            }
          : { clearThumbnail: true }),
      },
    };
  }
  if (typeId === 3) {
    const hasBadgeMedia = form.badge.media.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      badge: {
        ...(hasBadgeMedia
          ? {
              media: {
                mediaTypeId:       BADGE_MEDIA_TYPE_ID,
                linkLocalizations: form.badge.media.linkLocalizations,
              },
            }
          : { clearMedia: true }),
      },
    };
  }
  if (typeId === 6) {
    const hasThumbnail = form.avatar.thumbnailMedia.linkLocalizations.some(l => l.mediaLink?.trim());
    return {
      ...base,
      avatar: {
        assetUrl: form.avatar.assetUrl || null,
        ...(hasThumbnail && {
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: form.avatar.thumbnailMedia.linkLocalizations,
          },
        }),
      },
    };
  }
  return base;
};

// ── Merchandise type/category constants ───────────────────────────────────────
const POS_COLOR   = { Head: '#7c3aed', Neck: '#0891b2', Top: '#1d4ed8', Bottom: '#15803d', Hand: '#b45309', Feet: '#0f766e', FullBody: '#6d28d9', Accessory: '#be185d' };
const POSITIONS   = ['Head', 'Neck', 'Top', 'Bottom', 'Hand', 'Feet', 'FullBody', 'Accessory'];
const INTERACTIONS = ['Yes', 'No'];

// ── Shared sub-components ─────────────────────────────────────────────────────
const FieldLabel = ({ children }) => (
  <Typography
    fontSize={10} fontWeight={700}
    sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.625, display: 'block' }}
  >
    <LocalText>{children}</LocalText>
  </Typography>
);

const inputSx = {
  ...textFieldSx,
  '& .MuiInputBase-input': { fontSize: 13, py: 1.125 },
};

const TypeSectionHeader = ({ color, bgcolor, border, children }) => (
  <Box sx={{ bgcolor, border: `1px solid ${border}`, borderRadius: '8px', px: 1.5, py: 1, mb: 1.75 }}>
    <Typography fontSize={10} fontWeight={800} sx={{ color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      <LocalText>{children}</LocalText>
    </Typography>
  </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────
const ItemModal = ({ open, onClose, onSave, initialData, itemTypes = [], taxCategories = [], merchandiseTypes = [], merchandiseCategories = [], ticketTypes = [], events = [], onAddTaxClass, onRefreshMerchandiseTypes, onRefreshMerchandiseCategories, readOnly = false, lockTypeId = null, forcePrizeOnly = false }) => {
  const isEdit = Boolean(initialData?.itemId);
  // forcePrizeOnly (Prizes tab on Items.jsx) does NOT lock the type — a prize can be any
  // of the same real catalog types (Merchandise/Ticket/Badge) the Item Catalog tab offers;
  // "prize" only means is_price_only=true + price forced to 0 below, decoupled from type
  // entirely. lockTypeId is a separate mechanism still used by the Package "Create New
  // Avatar" flow, which does pre-select exactly one type for its own reasons.
  const effectiveLockTypeId = lockTypeId;

  const { activeCurrency, currencies } = useCurrency();
  const { locale } = useLocalization();
  // Edit: use item's saved currency. Create: use space active currency.
  const itemCurrencyId   = isEdit ? (initialData?.prices?.[0]?.currencyId ?? null) : null;
  const resolvedCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
  const symbol           = resolvedCurrency?.symbol ?? '';
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const priceStep        = decimalPlaces > 0 ? (1 / factor).toFixed(decimalPlaces) : '1';

  // Localized placeholder strings used in non-JSX props
  const taxNamePlaceholder = useLocalText('e.g. state luxury tax');
  const addTaxTitle        = useLocalText('Add Custom Tax Category');

  const [form,       setForm]       = useState(makeEmptyForm);
  const [saving,     setSaving]     = useState(false);
  const [showTaxAdd, setShowTaxAdd] = useState(false);
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxRate, setNewTaxRate] = useState('');
  const [addingTax,  setAddingTax]  = useState(false);
  const [taxError,   setTaxError]   = useState('');
  const [thumbnailPreviewOpen, setThumbnailPreviewOpen] = useState(false);
  const [ticketThumbnailPreviewOpen, setTicketThumbnailPreviewOpen] = useState(false);
  const [badgeValid,           setBadgeValid]           = useState(true);

  // Merchandise type inline CRUD
  const [typeSelectOpen,    setTypeSelectOpen]    = useState(false);
  const [showMerchTypeAdd,  setShowMerchTypeAdd]  = useState(false);
  const [newMerchType,      setNewMerchType]      = useState({ type: '', position: 'Top', interaction: 'No' });
  const [editingMerchTypeId, setEditingMerchTypeId] = useState(null);
  const [editMerchType,     setEditMerchType]     = useState({ type: '', position: 'Top', interaction: 'No' });
  const [savingMerchType,   setSavingMerchType]   = useState(false);
  const [merchTypeError,    setMerchTypeError]    = useState('');
  const [deleteConfirm,     setDeleteConfirm]     = useState(null); // { kind: 'type'|'category', id, name }

  // Merchandise category inline CRUD
  const [catSelectOpen,     setCatSelectOpen]     = useState(false);
  const [showMerchCatAdd,   setShowMerchCatAdd]   = useState(false);
  const [newMerchCat,       setNewMerchCat]       = useState('');
  const [editingMerchCatId, setEditingMerchCatId] = useState(null);
  const [editMerchCat,      setEditMerchCat]      = useState('');
  const [savingMerchCat,    setSavingMerchCat]    = useState(false);
  const [merchCatError,     setMerchCatError]     = useState('');

  // Reset / populate form when modal opens
  useEffect(() => {
    if (!open) return;
    setShowTaxAdd(false);
    setNewTaxName('');
    setNewTaxRate('');
    setTaxError('');
    if (initialData) {
      setForm({
        itemTypeId:               initialData.itemTypeId ?? '',
        isAvailable:              initialData.isAvailable ?? true,
        nameKey:                  initialData.nameLocalizations?.key ?? '',
        descriptionKey:           initialData.descriptionLocalizations?.key ?? '',
        nameLocalizations:        initialData.nameLocalizations?.values        ?? [{ localeId: 'en_us', value: '' }],
        descriptionLocalizations: initialData.descriptionLocalizations?.values ?? [{ localeId: 'en_us', value: '' }],
        baseCost: initialData.prices?.[0]?.baseCost != null
                  ? String(initialData.prices[0].baseCost / factor)
                  : initialData.baseCost != null ? String(initialData.baseCost / factor) : '',
        taxId:   initialData.tax?.categoryId ?? '',
        taxMode: initialData.tax?.isInclusive != null
                  ? (initialData.tax.isInclusive ? 'Inclusive' : 'Exclusive')
                  : 'Exclusive',
        sku:     initialData.sku ?? '',
        merchandise: initialData.merchandise ? {
          merchandiseTypeId:    initialData.merchandise.merchandise_Type_Id ?? initialData.merchandise.merchandiseTypeId ?? '',
          merchandiseCategoryId: initialData.merchandise.merchandise_Category_Id ?? initialData.merchandise.merchandiseCategoryId ?? '',
          assetUrl:             initialData.merchandise.asset_Url ?? initialData.merchandise.assetUrl ?? '',
          thumbnailMedia: {
            mediaTypeId:       1,
            linkLocalizations: initialData.merchandise.thumbnail?.linkLocalizations
                            ?? initialData.merchandise.thumbnailMedia?.linkLocalizations
                            ?? [{ localeId: 'en_us', mediaLink: '' }],
          },
        } : {
          merchandiseTypeId: '', merchandiseCategoryId: '', assetUrl: '',
          thumbnailMedia: { mediaTypeId: 1, linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }] },
        },
        ticket: initialData.ticket ? {
          eventId:      initialData.ticket.event_Id ?? initialData.ticket.eventId ?? '',
          ticketTypeId: initialData.ticket.ticket_Type_Id ?? initialData.ticket.ticketTypeId ?? '',
          validFrom:    initialData.ticket.validFrom ? initialData.ticket.validFrom.slice(0, 10) : '',
          validTo:      initialData.ticket.validTo   ? initialData.ticket.validTo.slice(0, 10)   : '',
          stockQuantity: initialData.ticket.stockQuantity ?? initialData.ticket.stock_Quantity ?? '',
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: initialData.ticket.thumbnail?.linkLocalizations
                            ?? initialData.ticket.thumbnailMedia?.linkLocalizations
                            ?? [{ localeId: 'en_us', mediaLink: '' }],
          },
        } : {
          eventId: '', ticketTypeId: '', validFrom: '', validTo: '', stockQuantity: '',
          thumbnailMedia: { mediaTypeId: THUMBNAIL_MEDIA_TYPE_ID, linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }] },
        },
        badge:  initialData.badge  ?? {
          media: { mediaTypeId: 2, linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }] },
        },
        avatar: initialData.avatar ? {
          assetUrl: initialData.avatar.assetUrl ?? '',
          thumbnailMedia: {
            mediaTypeId:       THUMBNAIL_MEDIA_TYPE_ID,
            linkLocalizations: initialData.avatar.thumbnail?.linkLocalizations
                            ?? initialData.avatar.thumbnailMedia?.linkLocalizations
                            ?? [{ localeId: 'en_us', mediaLink: '' }],
          },
        } : {
          assetUrl: '',
          thumbnailMedia: { mediaTypeId: THUMBNAIL_MEDIA_TYPE_ID, linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }] },
        },
      });
    } else {
      // lockTypeId pre-selects the type for a caller that only ever creates one item
      // type (e.g. the Package flow's "Create New Avatar" shortcut) — forcePrizeOnly no
      // longer sets it, so the Prizes tab starts with an empty type just like the Item
      // Catalog tab does, but still defaults price to 0 (forced regardless of type below).
      setForm({
        ...makeEmptyForm(),
        itemTypeId: effectiveLockTypeId ?? '',
        ...(forcePrizeOnly && { baseCost: '0' }),
      });
    }
  }, [open, initialData, effectiveLockTypeId, forcePrizeOnly]);

  // Auto-generate SKU for new items when name or type changes
  const firstLocName     = form.nameLocalizations?.[0]?.value ?? '';
  const selectedTypeLabel = itemTypes.find(t => t.id === Number(form.itemTypeId))?.Type ?? '';
  useEffect(() => {
    if (!open || isEdit) return;
    if (firstLocName && form.itemTypeId) {
      setForm(prev => ({ ...prev, sku: generateSKU(firstLocName, selectedTypeLabel) }));
    }
  }, [firstLocName, form.itemTypeId, open, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeId = Number(form.itemTypeId);

  const selectedTax = useMemo(
    () => taxCategories.find(t => t.id === form.taxId) ?? null,
    [taxCategories, form.taxId]
  );
  const pricing = useMemo(
    () => computePrice(form.baseCost, selectedTax?.ratePct ?? 0, form.taxMode),
    [form.baseCost, selectedTax, form.taxMode]
  );

  // ── Validity ──────────────────────────────────────────────────────────────
  const { status: textureUrlStatus, reason: textureUrlReason }     = useUrlValidation(readOnly ? '' : form.merchandise.assetUrl, 'image');
  const { status: thumbnailUrlStatus, reason: thumbnailUrlReason } = useUrlValidation(readOnly ? '' : (form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''), 'image');
  const { status: ticketThumbnailUrlStatus, reason: ticketThumbnailUrlReason } = useUrlValidation(readOnly ? '' : (form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''), 'image');

  const isTypeValid   = form.itemTypeId !== '';
  const isNameValid   = hasRequiredName(form.nameLocalizations);
  const priceNum      = parseFloat(form.baseCost);
  // Prize items are never priced — the price field is forced to 0 and hidden
  // (see forcePrizeOnly below), so it's excluded from validation entirely.
  const isPriceValid  = forcePrizeOnly || (form.baseCost !== '' && !Number.isNaN(priceNum) && priceNum > 0);
  const isAssetUrlValid = readOnly || textureUrlStatus === 'valid';
  const isMerchValid  = typeId !== 1 || (form.merchandise.merchandiseTypeId !== '' && form.merchandise.merchandiseCategoryId !== '' && isAssetUrlValid);
  const stockQuantityNum = toNullableInt(form.ticket.stockQuantity);
  // Thumbnail is required for Ticket, same as Merchandise's — it's used as the card cover
  // image for the ticket item, so every ticket needs one.
  const isTicketThumbnailValid = readOnly || ticketThumbnailUrlStatus === 'valid';
  const isTicketValid = typeId !== 2 || (
    form.ticket.eventId !== '' && form.ticket.ticketTypeId !== '' &&
    (stockQuantityNum === null || stockQuantityNum >= 0) &&
    isTicketThumbnailValid
  );
  const isThumbnailValid = typeId !== 1 || readOnly || thumbnailUrlStatus === 'valid';
  const isBadgeValid     = typeId !== 3 || badgeValid;
  // No live URL validation for Avatar's fields — decided 2026-09-15, plain required-non-empty check.
  const isAvatarValid = typeId !== 6 || (
    form.avatar.assetUrl?.trim() !== '' &&
    form.avatar.thumbnailMedia.linkLocalizations[0]?.mediaLink?.trim() !== ''
  );
  const isValid = isTypeValid && isNameValid && isMerchValid && isTicketValid && isPriceValid && isThumbnailValid && isBadgeValid && isAvatarValid;

  // Field change helpers
  const set      = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const setMerch = (field) => (e) => setForm(prev => ({ ...prev, merchandise: { ...prev.merchandise, [field]: e.target.value } }));
  const setTicket = (field) => (e) => setForm(prev => ({ ...prev, ticket: { ...prev.ticket, [field]: e.target.value } }));
  const setAvatar = (field) => (e) => setForm(prev => ({ ...prev, avatar: { ...prev.avatar, [field]: e.target.value } }));

  const handleTicketEventChange = (e) => {
    const eventId = e.target.value;
    const evt = events.find(ev => String(ev.id) === String(eventId));
    setForm(prev => ({
      ...prev,
      ticket: {
        ...prev.ticket,
        eventId,
        validFrom: evt?.scheduledStart ? evt.scheduledStart.slice(0, 10) : prev.ticket.validFrom,
        validTo:   evt?.scheduledEnd   ? evt.scheduledEnd.slice(0, 10)   : prev.ticket.validTo,
      },
    }));
  };

  const eventDisplayName = (evt) =>
    evt.localizedName?.values?.find(v => v.localeId === locale)?.value ||
    evt.localizedName?.values?.[0]?.value ||
    '';

  const selectedMerchType = useMemo(
    () => merchandiseTypes.find(mt => mt.id === Number(form.merchandise.merchandiseTypeId)) ?? null,
    [merchandiseTypes, form.merchandise.merchandiseTypeId]
  );

  const handleAddTaxClass = async () => {
    if (!newTaxName.trim() || !newTaxRate) return;
    const ratePct = parseFloat(newTaxRate);
    if (Number.isNaN(ratePct) || ratePct < 0 || ratePct > 100) {
      setTaxError('Tax percentage must be between 0 and 100.');
      return;
    }
    setAddingTax(true); setTaxError('');
    try {
      const created = await onAddTaxClass({ name: newTaxName.trim(), ratePct });
      setForm(prev => ({ ...prev, taxId: created.id }));
      setNewTaxName('');
      setNewTaxRate('');
      setShowTaxAdd(false);
    } catch (err) {
      setTaxError(extractErrorMessage(err));
    } finally {
      setAddingTax(false);
    }
  };

  // ── Merchandise type CRUD ─────────────────────────────────────────────────
  const handleAddMerchType = async () => {
    if (!newMerchType.type.trim()) { setMerchTypeError('Name is required.'); return; }
    setSavingMerchType(true); setMerchTypeError('');
    try {
      const created = await createMerchandiseType({ type: newMerchType.type.trim(), position: newMerchType.position, interaction: newMerchType.interaction });
      setNewMerchType({ type: '', position: 'Top', interaction: 'No' });
      setShowMerchTypeAdd(false);
      await onRefreshMerchandiseTypes?.();
      if (created?.id) setForm(prev => ({ ...prev, merchandise: { ...prev.merchandise, merchandiseTypeId: created.id } }));
    } catch (err) { setMerchTypeError(extractErrorMessage(err)); }
    finally { setSavingMerchType(false); }
  };

  const handleEditMerchType = (t, e) => {
    e.stopPropagation();
    setTypeSelectOpen(false);
    setEditingMerchTypeId(t.id);
    setEditMerchType({ type: t.Type ?? '', position: t.Position ?? 'Top', interaction: t.Interaction ?? 'No' });
    setShowMerchTypeAdd(false);
    setMerchTypeError('');
  };

  const handleSaveEditMerchType = async () => {
    if (!editMerchType.type.trim()) { setMerchTypeError('Name is required.'); return; }
    setSavingMerchType(true); setMerchTypeError('');
    try {
      await updateMerchandiseType(editingMerchTypeId, { type: editMerchType.type.trim(), position: editMerchType.position, interaction: editMerchType.interaction });
      setEditingMerchTypeId(null);
      await onRefreshMerchandiseTypes?.();
    } catch (err) { setMerchTypeError(extractErrorMessage(err)); }
    finally { setSavingMerchType(false); }
  };

  const handleDeleteMerchType = (id, e) => {
    e.stopPropagation();
    setTypeSelectOpen(false);
    const t = merchandiseTypes.find(mt => mt.id === id);
    setDeleteConfirm({ kind: 'type', id, name: t?.Type ?? 'this type' });
  };

  // ── Merchandise category CRUD ─────────────────────────────────────────────
  const handleAddMerchCat = async () => {
    if (!newMerchCat.trim()) { setMerchCatError('Name is required.'); return; }
    setSavingMerchCat(true); setMerchCatError('');
    try {
      const created = await createMerchandiseCategory({ category: newMerchCat.trim() });
      setNewMerchCat('');
      setShowMerchCatAdd(false);
      await onRefreshMerchandiseCategories?.();
      if (created?.id) setForm(prev => ({ ...prev, merchandise: { ...prev.merchandise, merchandiseCategoryId: created.id } }));
    } catch (err) { setMerchCatError(extractErrorMessage(err)); }
    finally { setSavingMerchCat(false); }
  };

  const handleEditMerchCat = (c, e) => {
    e.stopPropagation();
    setCatSelectOpen(false);
    setEditingMerchCatId(c.id);
    setEditMerchCat(c.Category ?? '');
    setShowMerchCatAdd(false);
    setMerchCatError('');
  };

  const handleSaveEditMerchCat = async () => {
    if (!editMerchCat.trim()) { setMerchCatError('Name is required.'); return; }
    setSavingMerchCat(true); setMerchCatError('');
    try {
      await updateMerchandiseCategory(editingMerchCatId, { category: editMerchCat.trim() });
      setEditingMerchCatId(null);
      await onRefreshMerchandiseCategories?.();
    } catch (err) { setMerchCatError(extractErrorMessage(err)); }
    finally { setSavingMerchCat(false); }
  };

  const handleDeleteMerchCat = (id, e) => {
    e.stopPropagation();
    setCatSelectOpen(false);
    const c = merchandiseCategories.find(mc => mc.id === id);
    setDeleteConfirm({ kind: 'category', id, name: c?.Category ?? 'this category' });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { kind, id } = deleteConfirm;
    setDeleteConfirm(null);
    if (kind === 'type') {
      setSavingMerchType(true);
      try {
        await deleteMerchandiseType(id);
        if (Number(form.merchandise.merchandiseTypeId) === id)
          setForm(prev => ({ ...prev, merchandise: { ...prev.merchandise, merchandiseTypeId: '' } }));
        await onRefreshMerchandiseTypes?.();
      } catch (err) { setMerchTypeError(extractErrorMessage(err)); }
      finally { setSavingMerchType(false); }
    } else {
      setSavingMerchCat(true);
      try {
        await deleteMerchandiseCategory(id);
        if (Number(form.merchandise.merchandiseCategoryId) === id)
          setForm(prev => ({ ...prev, merchandise: { ...prev.merchandise, merchandiseCategoryId: '' } }));
        await onRefreshMerchandiseCategories?.();
      } catch (err) { setMerchCatError(extractErrorMessage(err)); }
      finally { setSavingMerchCat(false); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || readOnly) return;
    setSaving(true);
    try {
      const saveCurrencyId = isEdit ? (resolvedCurrency?.id ?? activeCurrency?.id) : activeCurrency?.id;
      const payload = isEdit
        ? buildUpdatePayload(form, factor, saveCurrencyId, forcePrizeOnly)
        : buildPayload(form, factor, activeCurrency?.id, forcePrizeOnly);
      await onSave({ ...payload, itemId: initialData?.itemId, sku: form.sku });
    } finally {
      setSaving(false);
    }
  };

  // Discount preview — edit mode only, reads initialData.discounts (embedded in item response)
  const allDiscounts = isEdit ? (initialData?.discounts ?? []) : [];
  const DISC_STATUS_STYLE = {
    Active:   { bg: '#dcfce7', color: '#15803d' },
    Upcoming: { bg: '#dbeafe', color: '#1d4ed8' },
    Expired:  { bg: '#f1f5f9', color: '#64748b' },
  };
  const getDiscStatus = (d) => {
    const today = new Date().toISOString().slice(0, 10);
    const start = d.startAt ? String(d.startAt).slice(0, 10) : null;
    const end   = d.endAt   ? String(d.endAt).slice(0, 10)   : null;
    if (start && today < start) return 'Upcoming';
    if (end   && today > end)   return 'Expired';
    return 'Active';
  };
  const activeDiscountsForPreview = allDiscounts
    .filter(d => getDiscStatus(d) === 'Active')
    .sort((a, b) => (a.discountTypeId ?? 0) - (b.discountTypeId ?? 0));
  let priceAfterDiscounts = pricing.subtotal;
  for (const d of activeDiscountsForPreview) {
    if (d.ratePct != null)
      priceAfterDiscounts = priceAfterDiscounts * (1 - d.ratePct / 100);
    else if (d.amount != null)
      priceAfterDiscounts = Math.max(0, priceAfterDiscounts - d.amount / factor);
  }
  const fmtDiscDate = (dt) => dt ? dt.slice(0, 10) : '—';

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px', overflow: 'hidden',
          border: '1px solid #e2e8f0', maxHeight: '92vh',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          bgcolor: 'rgba(248,250,252,0.9)',
          borderBottom: '1px solid #f1f5f9',
          pt: 2.875, pb: 2.25, px: { xs: 2, sm: 3 },
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={15} sx={{ color: '#1e293b', lineHeight: 1.3 }}>
              <LocalText>{readOnly ? 'View Item' : isEdit ? 'Edit Item' : forcePrizeOnly ? 'Create Prize Item' : 'Create New Item'}</LocalText>
            </Typography>
            <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.2 }}>
              <LocalText>Configure item type, localizations, cost, and type-specific details.</LocalText>
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8', '&:hover': { color: '#475569', bgcolor: 'rgba(100,116,139,0.1)' }, flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
        <DialogContent sx={{ p: { xs: 2, sm: 2.75 }, overflowY: 'auto' }}>
          {/* pointerEvents lives on this inner wrapper, not DialogContent itself — putting it
              on DialogContent would block wheel/scrollbar hit-testing on the scroll container. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pointerEvents: readOnly ? 'none' : undefined, '& > *': { flexShrink: 0 } }}>

          {/* ── Item Type ───────────────────────────────────────────── */}
          <Box>
            <FieldLabel>Item Type *</FieldLabel>
            <FormControl size="small" required fullWidth disabled={isEdit || !!effectiveLockTypeId}>
              <Select
                value={form.itemTypeId}
                onChange={set('itemTypeId')}
                displayEmpty
                disabled={isEdit || !!effectiveLockTypeId}
                sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                MenuProps={dropdownMenuProps}
              >
                <MenuItem value="" disabled><em style={{ fontSize: 13 }}><LocalText>Select item type…</LocalText></em></MenuItem>
                {itemTypes.map(t => (
                  <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}><LocalText>{t.Type}</LocalText></MenuItem>
                ))}
              </Select>
              {!readOnly && !isEdit && !form.itemTypeId && <FormHelperText><LocalText>Required</LocalText></FormHelperText>}
            </FormControl>
          </Box>

          {/* ── Name + Description ──────────────────────────────────── */}
          <Box>
            <FieldLabel>Item Name *</FieldLabel>
            <DropdownTextField
              label="Item name"
              values={form.nameLocalizations}
              onChange={(locs) => setForm(prev => ({ ...prev, nameLocalizations: locs }))}
              attribute="value"
              required={!readOnly}
            />
          </Box>

          <Box>
            <FieldLabel>Description</FieldLabel>
            <DropdownTextField
              label="Description"
              values={form.descriptionLocalizations}
              onChange={(locs) => setForm(prev => ({ ...prev, descriptionLocalizations: locs }))}
              attribute="value"
              multiline
              rows={3}
            />
          </Box>

          {/* ── Availability ─────────────────────────────────────────── */}
          <Box>
            <FieldLabel>Availability</FieldLabel>
            <Box sx={{ display: 'flex', bgcolor: '#e2e8f0', p: '2px', borderRadius: '8px', border: '1px solid #cbd5e1', height: 36 }}>
              {[
                { value: true,  label: 'Available' },
                { value: false, label: 'Unavailable' },
              ].map(({ value, label }) => (
                <Box
                  key={label}
                  onClick={() => setForm(prev => ({ ...prev, isAvailable: value }))}
                  sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: '6px',
                    userSelect: 'none', transition: 'all 0.15s',
                    bgcolor: form.isAvailable === value ? 'white' : 'transparent',
                    color: form.isAvailable === value
                      ? (value ? '#15803d' : '#dc2626')
                      : '#64748b',
                    boxShadow: form.isAvailable === value ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                  }}
                >
                  <LocalText>{label}</LocalText>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ══ TYPE-SPECIFIC SECTIONS ════════════════════════════════ */}

          {/* ── Merchandise ─────────────────────────────────────────── */}
          {typeId === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TypeSectionHeader color="#4f46e5" bgcolor="#eef2ff" border="#c7d2fe">
                Merchandise Details
              </TypeSectionHeader>

              {/* Type group: 3-col grid + edit/add forms as one flex item (eliminates gap when collapsed) */}
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                  {/* Merchandise Type select + "+" */}
                  <Box>
                    <FieldLabel>Merchandise Type *</FieldLabel>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          open={typeSelectOpen}
                          onOpen={() => setTypeSelectOpen(true)}
                          onClose={() => setTypeSelectOpen(false)}
                          value={form.merchandise.merchandiseTypeId}
                          onChange={setMerch('merchandiseTypeId')}
                          displayEmpty
                          renderValue={(val) => {
                            if (!val && val !== 0) return <em style={{ fontSize: 13 }}>Select type…</em>;
                            const t = merchandiseTypes.find(mt => mt.id === Number(val));
                            return <span style={{ fontSize: 13 }}>{t ? t.Type : val}</span>;
                          }}
                          sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                          MenuProps={dropdownMenuProps}
                        >
                          {merchandiseTypes.map(t => (
                            <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: 0.5 }}>
                              <span style={{ flex: 1 }}>{t.Type}</span>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                                <IconButton size="small" onClick={(e) => handleEditMerchType(t, e)} sx={{ p: 0.375, color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' } }}>
                                  <EditOutlinedIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => handleDeleteMerchType(t.id, e)} sx={{ p: 0.375, color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        onClick={() => { setShowMerchTypeAdd(v => !v); setEditingMerchTypeId(null); setMerchTypeError(''); }}
                        title="Add merchandise type"
                        sx={{ color: '#4f46e5', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', width: 36, height: 36, flexShrink: 0, '&:hover': { bgcolor: '#e0e7ff' } }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    {!readOnly && !form.merchandise.merchandiseTypeId && <FormHelperText>Required</FormHelperText>}
                  </Box>

                  {/* Position (read-only TextField) */}
                  <Box>
                    <FieldLabel>Position</FieldLabel>
                    <TextField
                      size="small" fullWidth
                      value={selectedMerchType?.Position ?? ''}
                      placeholder="—"
                      InputProps={{ readOnly: true }}
                      sx={{ ...selectFieldSx, '& .MuiInputBase-input': { fontSize: 13, py: 1.125, cursor: 'default' }, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                    />
                  </Box>

                  {/* Interaction (read-only TextField) */}
                  <Box>
                    <FieldLabel>Interaction</FieldLabel>
                    <TextField
                      size="small" fullWidth
                      value={selectedMerchType?.Interaction ?? ''}
                      placeholder="—"
                      InputProps={{ readOnly: true }}
                      sx={{ ...selectFieldSx, '& .MuiInputBase-input': { fontSize: 13, py: 1.125, cursor: 'default' }, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                    />
                  </Box>
                </Box>

                {/* Edit type form — blue styled box, sibling to grid (not inside it) */}
                <Collapse in={editingMerchTypeId !== null}>
                  <Box sx={{ mt: 1, bgcolor: 'rgba(239,246,255,0.9)', p: 1.5, borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <Typography fontSize={9} fontWeight={800} sx={{ color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Edit Type</Typography>
                    {merchTypeError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{merchTypeError}</Typography>}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5, mb: 1 }}>
                      <TextField size="small" fullWidth value={editMerchType.type} onChange={e => setEditMerchType(f => ({ ...f, type: e.target.value }))} placeholder="Type name…" sx={inputSx} inputProps={{ style: { fontSize: 13 } }} />
                      <FormControl size="small" fullWidth>
                        <Select value={editMerchType.position} onChange={e => setEditMerchType(f => ({ ...f, position: e.target.value }))} sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }} MenuProps={dropdownMenuProps}>
                          {POSITIONS.map(p => <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <Select value={editMerchType.interaction} onChange={e => setEditMerchType(f => ({ ...f, interaction: e.target.value }))} sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }} MenuProps={dropdownMenuProps}>
                          {INTERACTIONS.map(i => <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{i}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button onClick={() => { setEditingMerchTypeId(null); setMerchTypeError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                      <Button variant="contained" size="small" onClick={handleSaveEditMerchType} disabled={savingMerchType} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' }, '&.Mui-disabled': { bgcolor: '#bfdbfe', color: 'white' } }}>Save</Button>
                    </Box>
                  </Box>
                </Collapse>

                {/* Add type form — blue styled box, sibling to grid (not inside it) */}
                <Collapse in={showMerchTypeAdd}>
                  <Box sx={{ mt: 1, bgcolor: 'rgba(239,246,255,0.9)', p: 1.5, borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <Typography fontSize={9} fontWeight={800} sx={{ color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>New Type</Typography>
                    {merchTypeError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{merchTypeError}</Typography>}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5, mb: 1 }}>
                      <TextField size="small" fullWidth value={newMerchType.type} onChange={e => setNewMerchType(f => ({ ...f, type: e.target.value }))} placeholder="Type name…" sx={inputSx} inputProps={{ style: { fontSize: 13 } }} autoFocus />
                      <FormControl size="small" fullWidth>
                        <Select value={newMerchType.position} onChange={e => setNewMerchType(f => ({ ...f, position: e.target.value }))} sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }} MenuProps={dropdownMenuProps}>
                          {POSITIONS.map(p => <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <Select value={newMerchType.interaction} onChange={e => setNewMerchType(f => ({ ...f, interaction: e.target.value }))} sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }} MenuProps={dropdownMenuProps}>
                          {INTERACTIONS.map(i => <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{i}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button onClick={() => { setShowMerchTypeAdd(false); setNewMerchType({ type: '', position: 'Top', interaction: 'No' }); setMerchTypeError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                      <Button variant="contained" size="small" onClick={handleAddMerchType} disabled={savingMerchType} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' }, '&.Mui-disabled': { bgcolor: '#bfdbfe', color: 'white' } }}>Add Type</Button>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* Row 2: Category + inline CRUD */}
              <Box>
                <FieldLabel>Merchandise Category *</FieldLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      open={catSelectOpen}
                      onOpen={() => setCatSelectOpen(true)}
                      onClose={() => setCatSelectOpen(false)}
                      value={form.merchandise.merchandiseCategoryId}
                      onChange={setMerch('merchandiseCategoryId')}
                      displayEmpty
                      renderValue={(val) => {
                        if (!val && val !== 0) return <em style={{ fontSize: 13 }}>Select category…</em>;
                        const c = merchandiseCategories.find(mc => mc.id === Number(val));
                        return <span style={{ fontSize: 13 }}>{c ? c.Category : val}</span>;
                      }}
                      sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                      MenuProps={dropdownMenuProps}
                    >
                      {merchandiseCategories.map(c => (
                        <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: 0.5 }}>
                          <span style={{ flex: 1 }}>{c.Category}</span>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                            <IconButton size="small" onClick={(e) => handleEditMerchCat(c, e)} sx={{ p: 0.375, color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' } }}>
                              <EditOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => handleDeleteMerchCat(c.id, e)} sx={{ p: 0.375, color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => { setShowMerchCatAdd(v => !v); setEditingMerchCatId(null); setMerchCatError(''); }}
                    title="Add merchandise category"
                    sx={{ color: '#4f46e5', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', width: 36, height: 36, flexShrink: 0, '&:hover': { bgcolor: '#e0e7ff' } }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {!readOnly && !form.merchandise.merchandiseCategoryId && <FormHelperText>Required</FormHelperText>}

                {/* Edit category form */}
                <Collapse in={editingMerchCatId !== null}>
                  <Box sx={{ mt: 1, bgcolor: 'rgba(238,242,255,0.85)', p: 1.5, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                    <Typography fontSize={9} fontWeight={800} sx={{ color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Edit Category</Typography>
                    {merchCatError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{merchCatError}</Typography>}
                    <TextField size="small" fullWidth value={editMerchCat} onChange={e => setEditMerchCat(e.target.value)} placeholder="Category name…" sx={{ ...inputSx, mb: 1 }} inputProps={{ style: { fontSize: 12 } }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button onClick={() => { setEditingMerchCatId(null); setMerchCatError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                      <Button variant="contained" size="small" onClick={handleSaveEditMerchCat} disabled={savingMerchCat} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#c7d2fe', color: 'white' } }}>Save</Button>
                    </Box>
                  </Box>
                </Collapse>

                {/* Add category form */}
                <Collapse in={showMerchCatAdd}>
                  <Box sx={{ mt: 1, bgcolor: 'rgba(238,242,255,0.85)', p: 1.5, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                    <Typography fontSize={9} fontWeight={800} sx={{ color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>New Category</Typography>
                    {merchCatError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{merchCatError}</Typography>}
                    <TextField size="small" fullWidth value={newMerchCat} onChange={e => setNewMerchCat(e.target.value)} placeholder="Category name…" sx={{ ...inputSx, mb: 1 }} inputProps={{ style: { fontSize: 12 } }} autoFocus />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button onClick={() => { setShowMerchCatAdd(false); setNewMerchCat(''); setMerchCatError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                      <Button variant="contained" size="small" onClick={handleAddMerchCat} disabled={savingMerchCat} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#c7d2fe', color: 'white' } }}>Add Category</Button>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              <Box>
                <FieldLabel>Texture Map Url *</FieldLabel>
                <TextField
                  hiddenLabel
                  value={form.merchandise.assetUrl}
                  onChange={setMerch('assetUrl')}
                  fullWidth size="small"
                  error={!readOnly && textureUrlStatus === 'invalid'}
                  sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace' } }}
                  placeholder="https://cdn.example.com/texture.png"
                  InputProps={{
                    endAdornment: !readOnly && (
                      <InputAdornment position="end">
                        <UrlStatusIcon status={textureUrlStatus} reason={textureUrlReason} />
                      </InputAdornment>
                    ),
                  }}
                />
                {!readOnly && (
                  <FormHelperText error={textureUrlStatus === 'invalid'}>
                    {!form.merchandise.assetUrl?.trim() ? 'Required'
                      : describeMediaUrlStatus(form.merchandise.assetUrl, textureUrlStatus, textureUrlReason, 'image')}
                  </FormHelperText>
                )}
              </Box>

              <Box>
                <FieldLabel>Thumbnail Image *</FieldLabel>
                <TextField
                  hiddenLabel
                  value={form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    merchandise: {
                      ...prev.merchandise,
                      thumbnailMedia: {
                        ...prev.merchandise.thumbnailMedia,
                        linkLocalizations: [{ localeId: 'en_us', mediaLink: e.target.value }],
                      },
                    },
                  }))}
                  fullWidth size="small"
                  error={!readOnly && thumbnailUrlStatus === 'invalid'}
                  sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace' } }}
                  placeholder="https://cdn.example.com/thumbnail.jpg"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {!readOnly && <UrlStatusIcon status={thumbnailUrlStatus} reason={thumbnailUrlReason} />}
                        {!readOnly && (
                          <IconButton
                            size="small"
                            onClick={() => setThumbnailPreviewOpen(true)}
                            disabled={!form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink}
                            title="Preview thumbnail"
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                {!readOnly && (
                  <FormHelperText error={thumbnailUrlStatus === 'invalid'}>
                    {!form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink?.trim() ? 'Required'
                      : describeMediaUrlStatus(form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? '', thumbnailUrlStatus, thumbnailUrlReason, 'image')}
                  </FormHelperText>
                )}
                <MediaPreviewOverlay
                  open={thumbnailPreviewOpen}
                  onClose={() => setThumbnailPreviewOpen(false)}
                  type="image"
                  url={form.merchandise.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''}
                />
              </Box>
            </Box>
          )}

          {/* ── Ticket ──────────────────────────────────────────────── */}
          {typeId === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TypeSectionHeader color="#059669" bgcolor="#f0fdf4" border="#a7f3d0">
                Ticket Details
              </TypeSectionHeader>
              <Box>
                <FieldLabel>Event *</FieldLabel>
                <FormControl size="small" required fullWidth>
                  <Select
                    value={form.ticket.eventId}
                    onChange={handleTicketEventChange}
                    displayEmpty
                    sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                    MenuProps={dropdownMenuProps}
                  >
                    <MenuItem value="" disabled><em style={{ fontSize: 13 }}><LocalText>Select event…</LocalText></em></MenuItem>
                    {events.length === 0 && (
                      <MenuItem value="" disabled sx={{ fontSize: 13 }}><LocalText>No events available</LocalText></MenuItem>
                    )}
                    {events.map(evt => (
                      <MenuItem key={evt.id} value={evt.id} sx={{ fontSize: 13 }}>{eventDisplayName(evt)}</MenuItem>
                    ))}
                  </Select>
                  {!readOnly && !form.ticket.eventId && <FormHelperText><LocalText>Required</LocalText></FormHelperText>}
                </FormControl>
              </Box>
              <Box>
                <FieldLabel>Ticket Type *</FieldLabel>
                <FormControl size="small" required fullWidth>
                  <Select
                    value={form.ticket.ticketTypeId}
                    onChange={setTicket('ticketTypeId')}
                    displayEmpty
                    sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                    MenuProps={dropdownMenuProps}
                  >
                    <MenuItem value="" disabled><em style={{ fontSize: 13 }}><LocalText>Select ticket type…</LocalText></em></MenuItem>
                    {ticketTypes.map(t => (
                      <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}><LocalText>{t.Type}</LocalText></MenuItem>
                    ))}
                  </Select>
                  {!readOnly && !form.ticket.ticketTypeId && <FormHelperText><LocalText>Required</LocalText></FormHelperText>}
                </FormControl>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <FieldLabel>Valid From</FieldLabel>
                  <CustomDatePicker
                    value={form.ticket.validFrom}
                    onChange={setTicket('validFrom')}
                    max={form.ticket.validTo || undefined}
                    placeholder="Select date"
                    fullWidth
                  />
                </Box>
                <Box>
                  <FieldLabel>Valid To</FieldLabel>
                  <CustomDatePicker
                    value={form.ticket.validTo}
                    onChange={setTicket('validTo')}
                    min={form.ticket.validFrom || undefined}
                    placeholder="Select date"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box>
                <FieldLabel>Total Available Seat</FieldLabel>
                <TextField
                  hiddenLabel type="number" value={form.ticket.stockQuantity ?? ''} onChange={setTicket('stockQuantity')}
                  size="small" fullWidth sx={inputSx}
                  inputProps={{ min: 0 }}
                  placeholder="Leave blank for unlimited"
                  helperText={
                    readOnly ? ''
                    : stockQuantityNum !== null && stockQuantityNum < 0 ? 'Cannot be negative'
                    : 'Blank means unlimited — enforced when tickets are purchased.'
                  }
                />
              </Box>
              <Box>
                <FieldLabel>Thumbnail Image *</FieldLabel>
                <TextField
                  hiddenLabel
                  value={form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    ticket: {
                      ...prev.ticket,
                      thumbnailMedia: {
                        ...prev.ticket.thumbnailMedia,
                        linkLocalizations: [{ localeId: 'en_us', mediaLink: e.target.value }],
                      },
                    },
                  }))}
                  fullWidth size="small"
                  error={!readOnly && ticketThumbnailUrlStatus === 'invalid'}
                  sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace' } }}
                  placeholder="https://cdn.example.com/thumbnail.jpg"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {!readOnly && <UrlStatusIcon status={ticketThumbnailUrlStatus} reason={ticketThumbnailUrlReason} />}
                        {!readOnly && (
                          <IconButton
                            size="small"
                            onClick={() => setTicketThumbnailPreviewOpen(true)}
                            disabled={!form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink}
                            title="Preview thumbnail"
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                {!readOnly && (
                  <FormHelperText error={ticketThumbnailUrlStatus === 'invalid'}>
                    {!form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink?.trim() ? 'Required'
                      : describeMediaUrlStatus(form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? '', ticketThumbnailUrlStatus, ticketThumbnailUrlReason, 'image')}
                  </FormHelperText>
                )}
                <MediaPreviewOverlay
                  open={ticketThumbnailPreviewOpen}
                  onClose={() => setTicketThumbnailPreviewOpen(false)}
                  type="image"
                  url={form.ticket.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''}
                />
              </Box>
            </Box>
          )}

          {/* ── Badge ───────────────────────────────────────────────── */}
          {typeId === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TypeSectionHeader color="#d97706" bgcolor="#fffbeb" border="#fcd34d">
                Badge Details
              </TypeSectionHeader>
              <Box>
                <FieldLabel>Badge Image</FieldLabel>
                <DropdownTextField
                  label="Badge image URL"
                  values={form.badge.media.linkLocalizations}
                  onChange={(locs) => setForm(prev => ({
                    ...prev,
                    badge: { ...prev.badge, media: { ...prev.badge.media, linkLocalizations: locs } },
                  }))}
                  attribute="mediaLink"
                  urlValidationType="image"
                  previewable
                  mediaType="image"
                  onValidationChange={setBadgeValid}
                />
              </Box>
            </Box>
          )}

          {/* ── Avatar ──────────────────────────────────────────────── */}
          {typeId === 6 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TypeSectionHeader color="#0d9488" bgcolor="#f0fdfa" border="#99f6e4">
                Avatar Details
              </TypeSectionHeader>

              <Box>
                <FieldLabel>Texture Map Url *</FieldLabel>
                <TextField
                  hiddenLabel
                  value={form.avatar.assetUrl}
                  onChange={(e) => setAvatar('assetUrl')({ target: { value: e.target.value.trim() } })}
                  fullWidth size="small"
                  sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace' } }}
                  placeholder="https://cdn.example.com/avatar.glb"
                  helperText={!readOnly && !form.avatar.assetUrl?.trim() ? 'Required' : ''}
                />
              </Box>

              <Box>
                <FieldLabel>Thumbnail Image *</FieldLabel>
                <TextField
                  hiddenLabel
                  value={form.avatar.thumbnailMedia.linkLocalizations[0]?.mediaLink ?? ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    avatar: {
                      ...prev.avatar,
                      thumbnailMedia: {
                        ...prev.avatar.thumbnailMedia,
                        linkLocalizations: [{ localeId: 'en_us', mediaLink: e.target.value.trim() }],
                      },
                    },
                  }))}
                  fullWidth size="small"
                  sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'monospace' } }}
                  placeholder="https://cdn.example.com/thumbnail.jpg"
                  helperText={!readOnly && !form.avatar.thumbnailMedia.linkLocalizations[0]?.mediaLink?.trim() ? 'Required' : ''}
                />
              </Box>
            </Box>
          )}

          {/* ── Item Price ───────────────────────────────────────────── */}
          {/* forcePrizeOnly (Prizes tab): a prize item is never purchasable, so the
              price field is force-set to 0 (see the "else" branch of the form-reset
              effect above) and hidden entirely, rather than just disabled — showing a
              locked-at-0 price field would only invite confusion for something that's
              never actually charged. */}
          {!forcePrizeOnly && (
          <Box>
            <FieldLabel>Item Price *</FieldLabel>
            <TextField
              hiddenLabel type="number"
              value={form.baseCost}
              onChange={set('baseCost')}
              required size="small" fullWidth sx={inputSx}
              inputProps={{ min: priceStep, step: priceStep }}
              InputProps={{ startAdornment: <Typography sx={{ color: '#64748b', mr: 0.5, fontSize: 13, fontWeight: 700 }}>{symbol}</Typography> }}
              placeholder="0.00"
              helperText={
                readOnly ? ''
                : form.baseCost === '' ? 'Required'
                : priceNum <= 0 ? 'Price must be greater than 0'
                : ''
              }
            />
          </Box>
          )}

          {/* ── Tax Category + Mode ──────────────────────────────────── */}
          {!forcePrizeOnly && (
          <Box sx={{ bgcolor: '#f8fafc', p: 1.75, borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <FieldLabel>Tax Category</FieldLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={form.taxId}
                      onChange={set('taxId')}
                      displayEmpty
                      sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1, fontSize: 12 } }}
                      MenuProps={dropdownMenuProps}
                    >
                      <MenuItem value=""><em style={{ fontSize: 12 }}><LocalText>None</LocalText></em></MenuItem>
                      {taxCategories.map(tc => (
                        <MenuItem key={tc.id} value={tc.id} sx={{ fontSize: 12 }}><LocalText>{tc.name}</LocalText> ({tc.ratePct}%)</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => { setShowTaxAdd(v => !v); setTaxError(''); }}
                    title={addTaxTitle}
                    sx={{ color: '#4f46e5', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', width: 36, height: 36, flexShrink: 0, '&:hover': { bgcolor: '#e0e7ff' } }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <FieldLabel>Tax Mode</FieldLabel>
                <Box sx={{ display: 'flex', bgcolor: '#e2e8f0', p: '2px', borderRadius: '8px', border: '1px solid #cbd5e1', height: 36 }}>
                  {['Exclusive', 'Inclusive'].map(mode => (
                    <Box
                      key={mode}
                      onClick={() => setForm(prev => ({ ...prev, taxMode: mode }))}
                      sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: '6px', userSelect: 'none', transition: 'all 0.15s',
                        bgcolor: form.taxMode === mode ? 'white' : 'transparent',
                        color:   form.taxMode === mode ? '#1e1b4b' : '#64748b',
                        boxShadow: form.taxMode === mode ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                      }}
                    >
                      <LocalText>{mode}</LocalText>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Collapse in={showTaxAdd}>
              <Box sx={{ bgcolor: 'rgba(238,242,255,0.85)', p: 1.75, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                <Typography fontSize={9} fontWeight={800} sx={{ color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BalanceOutlinedIcon sx={{ fontSize: 12 }} /> <LocalText>Create Custom Tax Category</LocalText>
                </Typography>
                {taxError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{taxError}</Typography>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1.5 }}>
                  <TextField hiddenLabel value={newTaxName} onChange={e => setNewTaxName(e.target.value)} size="small" fullWidth sx={inputSx} placeholder={taxNamePlaceholder} />
                  <TextField
                    hiddenLabel type="number" value={newTaxRate} onChange={e => setNewTaxRate(e.target.value)}
                    size="small" sx={{ ...inputSx, width: { xs: '100%', sm: 90 } }}
                    inputProps={{ min: 0, max: 100, step: '0.01' }} placeholder="0.00"
                    InputProps={{ endAdornment: <Typography fontSize={11} sx={{ color: '#94a3b8', fontWeight: 700 }}>%</Typography> }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                  <Button onClick={() => { setShowTaxAdd(false); setTaxError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>
                    <LocalText>Cancel</LocalText>
                  </Button>
                  <Button
                    variant="contained" size="small" onClick={handleAddTaxClass}
                    disabled={addingTax || !newTaxName.trim() || !newTaxRate}
                    sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#c7d2fe', color: 'white' } }}
                  >
                    <LocalText>Register Tax Category</LocalText>
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Box>
          )}

          {/* ── Price preview panel ─────────────────────────────────── */}
          {/* flexShrink: 0 — see PackageModal.jsx's identical panel for why this matters:
              without it, overflow:'hidden' below resets this Box's flex min-height to 0, so
              a tall dialog gets this box squeezed to near-nothing instead of scrolling normally.
              Hidden entirely under forcePrizeOnly — a prize item has no price, so a "Customer
              Pays" cost summary would be meaningless noise on that form. */}
          {!forcePrizeOnly && (
          <Box sx={{ background: '#0f172a', borderRadius: '12px', p: 2.25, color: 'white', overflow: 'hidden', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Typography fontSize={9} fontWeight={800} sx={{ color: '#c7d2fe', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <LocalText>Catalog Registry Costing</LocalText>
              </Typography>
              <Box sx={{ bgcolor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', px: 1.25, py: 0.3, borderRadius: '4px' }}>
                <Typography fontSize={9} fontWeight={700} sx={{ color: '#93c5fd' }}><LocalText>Pre-Discount Summary</LocalText></Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pb: 1.25, mb: 1.25, borderBottom: '1px solid rgba(99,102,241,0.25)' }}>
              <Typography fontSize={11.5} sx={{ color: '#e0e7ff' }}><LocalText>Customer Pays:</LocalText></Typography>
              <Typography fontSize={20} fontWeight={900} sx={{ color: '#34d399' }}>{symbol}{pricing.subtotal.toFixed(decimalPlaces)}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, fontFamily: 'monospace' }}>
              {[
                { key: 'base',    label: 'Base',           value: `${symbol}${(parseFloat(form.baseCost) || 0).toFixed(decimalPlaces)}` },
                { key: 'net',     label: 'Net (excl. tax)', value: `${symbol}${pricing.price.toFixed(decimalPlaces)}` },
                { key: 'tax',     label: 'Tax',            value: `+${symbol}${pricing.taxAmount.toFixed(decimalPlaces)}`, color: '#fbbf24' },
                { key: 'mode',    label: 'Mode',           value: form.taxMode, color: '#a5b4fc' },
              ].map(({ key, label, value, color }) => (
                <Box key={key}>
                  <Typography fontSize={8} sx={{ color: 'rgba(199,210,254,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                    <LocalText>{label}</LocalText>
                  </Typography>
                  <Typography fontSize={10} fontWeight={700} sx={{ color: color ?? 'white' }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          )}

          {/* ── Discount Campaigns (edit mode only) ─────────── */}
          {isEdit && allDiscounts.length > 0 && (
            <Box sx={{ border: '1px solid #fde68a', borderRadius: '12px', overflow: 'hidden' }}>
              {/* Header row */}
              <Box sx={{ bgcolor: '#fffbeb', px: 1.75, py: 1.125, borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontSize={9} fontWeight={800} sx={{ color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  <LocalText>Discount Campaigns</LocalText> ({allDiscounts.length})
                </Typography>
                {activeDiscountsForPreview.length > 0 && (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Typography fontSize={10} sx={{ color: '#64748b' }}><LocalText>After discounts:</LocalText></Typography>
                    <Typography fontSize={13} fontWeight={800} sx={{ color: '#15803d' }}>
                      {symbol}{priceAfterDiscounts.toFixed(decimalPlaces)}
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* One row per discount */}
              <Box>
                {allDiscounts.map((d, i) => {
                  const status = getDiscStatus(d);
                  const style  = DISC_STATUS_STYLE[status];
                  const label  = d.discountTypeId === 1
                    ? `${d.ratePct}% Off`
                    : d.amount != null ? `${symbol}${(d.amount / factor).toFixed(decimalPlaces)} Flat` : '—';
                  return (
                    <Box
                      key={i}
                      sx={{
                        px: 1.75, py: 1.125,
                        borderBottom: i < allDiscounts.length - 1 ? '1px solid #fef3c7' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ px: 1.25, py: 0.25, borderRadius: '999px', bgcolor: style.bg, color: style.color, fontSize: 9, fontWeight: 800 }}>
                          <LocalText>{status}</LocalText>
                        </Box>
                        <Typography fontSize={12} fontWeight={700} sx={{ color: '#1e293b' }}>{label}</Typography>
                      </Box>
                      <Typography fontSize={10} sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                        {fmtDiscDate(d.startAt)} → {fmtDiscDate(d.endAt)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          </Box>
        </DialogContent>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2.75, pt: 1.5, borderTop: '1px solid #f1f5f9', gap: 1.5, flexShrink: 0, pointerEvents: 'auto' }}>
          {readOnly ? (
            <Button
              onClick={onClose}
              sx={{ color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'none', '&:hover': { color: '#475569', bgcolor: '#f8fafc' } }}
            >
              <LocalText>Close</LocalText>
            </Button>
          ) : (
            <>
              <Button
                onClick={onClose}
                sx={{ color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'none', '&:hover': { color: '#475569', bgcolor: '#f8fafc' } }}
              >
                <LocalText>Discard</LocalText>
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !isValid}
                sx={{
                  bgcolor: '#2563eb', fontWeight: 700, fontSize: 12, textTransform: 'none',
                  borderRadius: '8px', px: 3, py: 1.125,
                  boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                  '&:hover': { bgcolor: '#1d4ed8', boxShadow: '0 4px 12px rgba(37,99,235,0.45)' },
                  '&.Mui-disabled': { bgcolor: '#bfdbfe', color: 'white' },
                }}
              >
                <LocalText>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}</LocalText>
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    <ConfirmModal
      open={deleteConfirm !== null}
      onCancel={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      severity="danger"
      title={deleteConfirm?.kind === 'type' ? 'Delete Merchandise Type' : 'Delete Merchandise Category'}
      message={`Remove "${deleteConfirm?.name}"?`}
      subtitle="This action cannot be undone."
      confirmLabel="Delete"
    />
    </>
  );
};

export default ItemModal;
